import Box from '@mui/material/Box';
import Button from 'common/components/Elements/Button/Button';
import { DownloadIcon } from 'common/components/Elements/Icon/Icon';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal, { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import { buildFileName, downloadImgFile } from 'common/utilities/browser/downloadFile/downloadFile';
import Commands from 'core/components/Elements/Commands/Commands';
import type { EnhancedCommand } from 'core/utilities/core.types';
import * as htmlToImage from 'html-to-image';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';
import isEqual from 'react-fast-compare';
import type { SequenceType } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import Colors from 'sequenceAnalyser/components/Colors/Colors/Colors';
import type { SelectedColorScheme } from 'sequenceAnalyser/utilities/colors';

interface ComponentProps {
  open: boolean;
  onOpen: (open: boolean) => void;
  onColorSchemeChange: (scheme?: SelectedColorScheme) => void;
  colors?: SelectedColorScheme;
  sessionSequenceType: SequenceType;
}

function ColorsModal({
  open, onOpen, onColorSchemeChange, colors, sessionSequenceType,
}: ComponentProps) {
  const [initialColors] = useState(colors);

  const revertColorsCommand: EnhancedCommand = {
    id: 'close',
    title: 'Discard Colors Change',
    active: !isEqual(colors, initialColors),
    label: 'Discard Colors Change',
    onClick: () => {
      onColorSchemeChange(initialColors);
      onOpen(false);
    },
    commands: [],
  };

  const closeCommand: EnhancedCommand = {
    id: 'close',
    title: 'Close',
    active: true,
    label: 'Close',
    onClick: () => onOpen(false),
    commands: [],
  };

  return (
    <SimpleModal
      title={I18n.SequenceAnalyser.TableActions.ColorsModal.title}
      open={open}
      onClose={() => {
        onOpen(false);
      }}
      draggablePoppover
      width={500}
      headerActions={(
        <Button
          action="secondary"
          title={I18n.SequenceAnalyser.TableActions.ColorsModal.download}
          onClick={async () => {
            const el = document.querySelector('#colorsResidues') as HTMLDivElement | undefined;
            if (el) {
              const fileDataUrl = await htmlToImage.toPng(
                el, { pixelRatio: 1, backgroundColor: '#ffffff' },
              );

              const { fileName } = I18n.SequenceAnalyser.TableActions.ColorsModal;

              downloadImgFile(buildFileName(fileName, 'png'), fileDataUrl);
            }
          }}
          buttonIcon
          disabled={!colors}
          small
        >
          <DownloadIcon />
        </Button>
      )}
      content={(
        <Box mt={1}>
          <ScrollableModalContent>
            <Colors
              colors={colors}
              onColorSchemeChange={onColorSchemeChange}
              sessionSequenceType={sessionSequenceType}
            />
          </ScrollableModalContent>

          <ModalActions
            rightActions={(
              <Commands
                id="ColorsModalCommands"
                disableResponsive
                commands={[revertColorsCommand, closeCommand]}
              />
            )}
          />
        </Box>
      )}
    />
  );
}

export default ColorsModal;
